// src/features/services/logic.ts
// Framework-agnostic business logic for entity service bindings.
// Zero React imports — pure TypeScript functions and data maps.

import type { ServiceDefinition, ServiceBinding, ServiceSummaryItem } from './types';

// ---------------------------------------------------------------------------
// Service Registry — maps product slugs to UI metadata + config extractors
// ---------------------------------------------------------------------------

export const SERVICE_REGISTRY: Record<string, ServiceDefinition> = {
  document: {
    slugs: ['document', 'core.document'],
    titleKey: 'services.document.title',
    descriptionKey: 'services.document.description',
    iconKey: 'file-text',
    getSummary: (policy: any): ServiceSummaryItem[] => {
      const items: ServiceSummaryItem[] = [];
      if (policy?.numbering_scheme_id) {
        items.push({
          labelKey: 'services.document.numbering',
          value: policy.numbering_prefix
            ? `${policy.numbering_prefix}-{seq}`
            : policy.numbering_scheme_id,
        });
      }
      if (policy?.require_approval) {
        items.push({
          labelKey: 'services.document.approval',
          value: 'required',
        });
      }
      if (policy?.enable_versioning) {
        items.push({
          labelKey: 'services.document.versioning',
          value: 'enabled',
        });
      }
      return items;
    },
    configRoute: 'documents',
  },
  task: {
    slugs: ['task', 'core.task'],
    titleKey: 'services.task.title',
    descriptionKey: 'services.task.description',
    iconKey: 'check-square',
    getSummary: (config: any): ServiceSummaryItem[] => {
      const items: ServiceSummaryItem[] = [];
      if (config?.workflow) {
        items.push({
          labelKey: 'services.task.workflow',
          value: config.workflow,
        });
      }
      return items;
    },
    configRoute: 'tasks',
  },
  calendar: {
    slugs: ['calendar', 'core.calendar'],
    titleKey: 'services.calendar.title',
    descriptionKey: 'services.calendar.description',
    iconKey: 'calendar',
    getSummary: (): ServiceSummaryItem[] => [],
    configRoute: 'calendar',
  },
  billing: {
    slugs: ['billing', 'core.billing'],
    titleKey: 'services.billing.title',
    descriptionKey: 'services.billing.description',
    iconKey: 'credit-card',
    getSummary: (): ServiceSummaryItem[] => [],
    configRoute: 'billing',
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Find service definition by product slug */
export function findServiceBySlug(slug: string): ServiceDefinition | undefined {
  for (const def of Object.values(SERVICE_REGISTRY)) {
    if (def.slugs.includes(slug)) return def;
  }
  return undefined;
}

/** Find service registry key by product slug */
export function findServiceKeyBySlug(slug: string): string | undefined {
  for (const [key, def] of Object.entries(SERVICE_REGISTRY)) {
    if (def.slugs.includes(slug)) return key;
  }
  return undefined;
}

/** Get all available services from tenant's subscribed products */
export function getAvailableServices(
  tenantProducts: Array<{ slug?: string; name?: string; id?: string }>,
): Array<{ key: string; definition: ServiceDefinition; product: any }> {
  const result: Array<{ key: string; definition: ServiceDefinition; product: any }> = [];

  for (const product of tenantProducts) {
    const slug = product.slug || product.name || '';
    for (const [key, def] of Object.entries(SERVICE_REGISTRY)) {
      if (def.slugs.some((s) => slug.toLowerCase().includes(s))) {
        result.push({ key, definition: def, product });
        break;
      }
    }
  }

  return result;
}

/** Fallback: return all services from the registry (when product matching fails) */
export function getAllServicesAsAvailable(): Array<{ key: string; definition: ServiceDefinition; product: any }> {
  return Object.entries(SERVICE_REGISTRY).map(([key, def]) => ({
    key,
    definition: def,
    product: { slug: def.slugs[0], name: key },
  }));
}

/** Get connected services for an entity (those with existing config/bindings) */
export function getConnectedServices(
  availableServices: Array<{ key: string; definition: ServiceDefinition; product: any }>,
  bindings: Record<string, ServiceBinding>,
): Array<{ key: string; definition: ServiceDefinition; product: any; binding: ServiceBinding }> {
  return availableServices
    .filter((s) => bindings[s.key]?.connected)
    .map((s) => ({ ...s, binding: bindings[s.key] }));
}

/** Check if a specific service is connected to an entity */
export function isServiceConnected(
  serviceKey: string,
  bindings: Record<string, ServiceBinding>,
): boolean {
  return !!bindings[serviceKey]?.connected;
}

/** Build service bindings map from known configurations */
export function buildServiceBindings(configs: {
  documentPolicy?: any;
  taskConfig?: any;
  calendarConfig?: any;
  billingConfig?: any;
}): Record<string, ServiceBinding> {
  const bindings: Record<string, ServiceBinding> = {};

  if (configs.documentPolicy) {
    bindings.document = {
      slug: 'document',
      connected: true,
      config: configs.documentPolicy,
    };
  }

  if (configs.taskConfig) {
    bindings.task = {
      slug: 'task',
      connected: true,
      config: configs.taskConfig,
    };
  }

  if (configs.calendarConfig) {
    bindings.calendar = {
      slug: 'calendar',
      connected: true,
      config: configs.calendarConfig,
    };
  }

  if (configs.billingConfig) {
    bindings.billing = {
      slug: 'billing',
      connected: true,
      config: configs.billingConfig,
    };
  }

  return bindings;
}
